/**
 * What the console says about an invoice 2W rebuilt on its own.
 *
 * A retailer refund that 2W writes into Shopify after a month has ended is billed in the month
 * the retailer settled it while that month's invoice is still a draft, and 2W then rebuilds the
 * draft so the refund is on it. Each rebuild leaves a record (the server's InvoiceAutoUpdate),
 * newest first on `invoice.autoUpdates`. Console only: brands never see these.
 *
 * Three kinds of record:
 *   LATE_RETAILER_REFUND             the draft was rebuilt; totals before and after. Each refund
 *                                     was added to this month, or moved out of it.
 *   LATE_RETAILER_REFUND_NOT_APPLIED the rebuild failed while the invoice is a draft, so the
 *                                     refunds are billed in this month but not on it. Red until
 *                                     a later rebuild - Regenerate included - sets resolvedAt.
 *   LATE_RETAILER_REFUND_MOVED       the invoice was already approved, so refunds settled in
 *                                     its month were billed in a later month instead. Nothing is
 *                                     missing; it says where they went.
 *
 * And when the month's commission adjustments change (approved, rejected, edited, deleted):
 *   COMMISSION_ADJUSTMENT              the draft was rebuilt; totals and adjustments before/after.
 *   COMMISSION_ADJUSTMENT_NOT_APPLIED  the rebuild failed: the report has the new adjustments and
 *                                      the draft does not. Red until a later rebuild.
 *
 * And two for the monthly storage count, taken at midnight on the 1st in the store's timezone,
 * which can land after the month's draft was made:
 *   STORAGE_COUNT                    the draft was rebuilt with the Storage Fees line.
 *   STORAGE_COUNT_NOT_APPLIED        the rebuild failed, so the storage fee is not on the draft.
 *                                     Red until a later rebuild sets resolvedAt.
 */

export const APPLIED = 'LATE_RETAILER_REFUND';
export const NOT_APPLIED = 'LATE_RETAILER_REFUND_NOT_APPLIED';
export const MOVED = 'LATE_RETAILER_REFUND_MOVED';
export const ADJUSTED = 'COMMISSION_ADJUSTMENT';
export const ADJUSTMENT_NOT_APPLIED = 'COMMISSION_ADJUSTMENT_NOT_APPLIED';
export const STORAGE = 'STORAGE_COUNT';
export const STORAGE_NOT_APPLIED = 'STORAGE_COUNT_NOT_APPLIED';

const updatesOf = (invoice) => (Array.isArray(invoice?.autoUpdates) ? invoice.autoUpdates : []);

/**
 * The badge beside an invoice's status, or null. A missing refund outranks a rebuild that
 * worked, because only the first needs anyone.
 */
export function autoUpdateBadge(invoice) {
  const updates = updatesOf(invoice);
  if (updates.some((u) => u.reason === NOT_APPLIED && !u.resolvedAt)) {
    return { label: 'Late refund not on invoice', tone: 'critical' };
  }
  if (updates.some((u) => u.reason === STORAGE_NOT_APPLIED && !u.resolvedAt)) {
    return { label: 'Storage fee not on invoice', tone: 'critical' };
  }
  if (updates.some((u) => u.reason === ADJUSTMENT_NOT_APPLIED && !u.resolvedAt)) {
    return { label: 'Adjustment not on invoice', tone: 'critical' };
  }
  if (updates.some((u) => [APPLIED, NOT_APPLIED, STORAGE, STORAGE_NOT_APPLIED, ADJUSTED, ADJUSTMENT_NOT_APPLIED].includes(u.reason))) {
    return { label: 'Auto-updated', tone: 'info' };
  }
  if (updates.some((u) => u.reason === MOVED)) {
    return { label: 'Late refund billed later', tone: undefined };
  }
  return null;
}

/** "1 late retailer refund" / "2 late retailer refunds". */
const refundCount = (n) => `${n} late retailer refund${n === 1 ? '' : 's'}`;

/** "2026-11" as "Nov 2026". */
const monthName = (ym) => {
  const [y, m] = String(ym || '').split('-').map(Number);
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return names[m - 1] ? `${names[m - 1]} ${y}` : String(ym || '');
};

/**
 * One record as the sentences the invoice preview shows.
 *
 * @param {object} update  One entry of invoice.autoUpdates.
 * @param {object} fmt     { currency(amount), date(value) } - the page's own formatters, so
 *                         figures read exactly as the rest of the invoice does.
 * @param {string} invoiceStatus  The invoice's status now, which decides what to do next.
 * @returns {{ title: string, lines: string[], tone: string }}
 */
export function describeAutoUpdate(update, fmt, invoiceStatus) {
  if (update?.reason === ADJUSTED || update?.reason === ADJUSTMENT_NOT_APPLIED) {
    const adj = update?.details?.adjustments || {};
    const change = `Commission adjustments ${fmt.currency(adj.before ?? 0)} -> ${fmt.currency(adj.after ?? 0)}`;
    if (update.reason === ADJUSTED) {
      return {
        tone: 'info',
        title: `Auto-updated ${fmt.date(update.createdAt)}: commission adjustments changed. `
          + `Total ${fmt.currency(update.totalBefore)} -> ${fmt.currency(update.totalAfter)}.`,
        lines: [change],
      };
    }
    if (update.resolvedAt) {
      return {
        tone: 'info',
        title: `${fmt.date(update.createdAt)}: changed commission adjustments could not be added automatically; `
          + `added when the invoice was rebuilt on ${fmt.date(update.resolvedAt)}.`,
        lines: [change],
      };
    }
    return {
      tone: 'critical',
      title: `${fmt.date(update.createdAt)}: the commission adjustments changed but this invoice was not rebuilt `
        + `(${update?.details?.why || 'not rebuilt'}). ${invoiceStatus === 'DRAFT' ? 'Press Regenerate to add them.' : 'Correct with an adjustment in a later month.'}`,
      lines: [change],
    };
  }
  if (update?.reason === STORAGE || update?.reason === STORAGE_NOT_APPLIED) {
    return describeStorageUpdate(update, fmt, invoiceStatus);
  }
  const refunds = Array.isArray(update?.details?.refunds) ? update.details.refunds : [];
  const lines = refunds.map((r) => {
    const order = r.orderNumber ? `Order ${r.orderNumber}` : `Shopify refund ${r.shopifyRefundId || '?'}`;
    const settled = r.settledAt ? `, settled by the retailer ${fmt.date(r.settledAt)}` : '';
    const where = r.movedTo ? `; billed in ${monthName(r.movedTo)}`
      : r.change === 'removed' ? '; moved out of this month' : '';
    return `${order}: ${fmt.currency(r.amount)}${settled}${where}`;
  });
  const added = refunds.filter((r) => r.change !== 'removed').length;
  const removed = refunds.length - added;

  if (update?.reason === NOT_APPLIED) {
    if (update.resolvedAt) {
      return {
        tone: 'info',
        title: `${fmt.date(update.createdAt)}: ${refundCount(refunds.length)} could not be added `
          + `automatically; added when the invoice was rebuilt on ${fmt.date(update.resolvedAt)}.`,
        lines,
      };
    }
    const action = invoiceStatus === 'DRAFT'
      ? 'Press Regenerate to add them.'
      : 'They are not deducted on this invoice; correct with an adjustment.';
    return {
      tone: 'critical',
      title: `${fmt.date(update.createdAt)}: ${refundCount(refunds.length)} billed in this month `
        + `${refunds.length === 1 ? 'is' : 'are'} not on this invoice (${update?.details?.why || 'not rebuilt'}). ${action}`,
      lines,
    };
  }

  if (update?.reason === MOVED) {
    return {
      tone: undefined,
      title: `${fmt.date(update.createdAt)}: ${refundCount(refunds.length)} settled in this month `
        + `came in after this invoice was ${String(invoiceStatus || 'approved').toLowerCase()}, `
        + 'so they were billed in a later month instead.',
      lines,
    };
  }

  const what = [added ? `${added} added` : '', removed ? `${removed} moved out` : ''].filter(Boolean).join(', ');
  return {
    tone: 'info',
    title: `Auto-updated ${fmt.date(update?.createdAt)}: late retailer refunds ${what}. `
      + `Total ${fmt.currency(update?.totalBefore)} -> ${fmt.currency(update?.totalAfter)}.`,
    lines,
  };
}

/** A storage-count record: "Storage count for Oct 2026: 2,435 units × $0.10 = $243.50". */
function describeStorageUpdate(update, fmt, invoiceStatus) {
  const st = update?.details?.storage || {};
  // A month that was never counted has no figures to show.
  const counted = st.fee !== undefined && st.fee !== null;
  const units = Number(st.unitsCharged) || 0;
  const rate = Number(st.ratePerUnit) || 0;
  const line = `Storage count for ${monthName(st.month)}: ${units.toLocaleString('en-US')} unit${units === 1 ? '' : 's'} `
    + `× $${rate.toFixed(4).replace(/0{1,2}$/, '')} = ${fmt.currency(st.fee)}`
    + (st.countedAt ? `, counted ${fmt.date(st.countedAt)}` : '')
    + (st.overrideNote ? ` (changed by hand: ${st.overrideNote})` : '');
  if (update.reason === STORAGE_NOT_APPLIED) {
    if (update.resolvedAt) {
      return {
        tone: 'info',
        title: `${fmt.date(update.createdAt)}: the storage fee could not be added automatically; `
          + `added on ${fmt.date(update.resolvedAt)}.`,
        lines: counted ? [line] : [],
      };
    }
    let action;
    if (invoiceStatus !== 'DRAFT') action = 'It is not charged on this invoice; correct with an adjustment.';
    else if (counted) action = 'Press Regenerate to add it.';
    else action = 'Count the month, or enter its units by hand, on the brand\'s Storage Fees card.';
    return {
      tone: 'critical',
      title: `${fmt.date(update.createdAt)}: the storage fee for this month is not on this invoice `
        + `(${update?.details?.why || 'not rebuilt'}). ${action}`,
      lines: counted ? [line] : [],
    };
  }
  // Rebuilt because shipping was switched: storage follows it.
  if (update?.details?.cause === 'SHIPPING_DISABLED' || update?.details?.cause === 'SHIPPING_ENABLED') {
    const off = update.details.cause === 'SHIPPING_DISABLED';
    // A month counted at $0 loses or regains only its line: say so rather than "$X -> $X".
    const same = Number(update.totalBefore) === Number(update.totalAfter);
    return {
      tone: 'info',
      title: `Auto-updated ${fmt.date(update.createdAt)}: storage ${same ? 'line' : 'fee'} ${off ? 'removed because shipping was disabled' : 'restored because shipping was enabled'}. `
        + (same ? `Total unchanged at ${fmt.currency(update.totalAfter)}.` : `Total ${fmt.currency(update.totalBefore)} -> ${fmt.currency(update.totalAfter)}.`),
      lines: [],
    };
  }
  return {
    tone: 'info',
    title: `Auto-updated ${fmt.date(update.createdAt)}: storage ${st.overrideNote ? 'units changed by hand' : 'counted'}. `
      + `Total ${fmt.currency(update.totalBefore)} -> ${fmt.currency(update.totalAfter)}.`,
    lines: [line],
  };
}

export default {
  autoUpdateBadge, describeAutoUpdate, APPLIED, NOT_APPLIED, MOVED, STORAGE, STORAGE_NOT_APPLIED,
  ADJUSTED, ADJUSTMENT_NOT_APPLIED,
};
